import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import HandleChange from '../../../../../../../Helpers/HandleChange';
import { toast } from 'react-toastify';
import routes from '../../../../../../../Routes/routes';
import {ACCESS_DEVICES_TABS, MODEL_DATA, STEPS_CREATE_SHIFT_ACCESS} from '../Model/Models';
import { ATTENDANCE_SUBVIEW_MODEL, TABS_ATTENDANCE } from "../../../Model/Model";

/**
 * Shared hook for shift access create/update flows.
 *
 * Mirrors the Devices CommonDevicesApp: centralizes form state, the local
 * client-side selection lists (devices / departments / users), their
 * "select for delete" buffers, step navigation and validation. Consumed by
 * both AddShiftAccessApp (create) and EditShiftAccessApp (update).
 *
 * @returns {Object} Shared state and handlers.
 */
const CommonShiftAccessApp = () => {

    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const { state } = location;
    const [modified, setModified] = useState(false);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    const [item, setItem] = useState({ ...MODEL_DATA });
    const [disabled, setDisabled] = useState(false);
    const [emptyFields, setEmptyFields] = useState(false);

    // Client-side selection lists + their delete buffers (one per section).
    const [devices, setDevices] = useState([]);
    const [selectDeleteDevices, setSelectDeleteDevices] = useState([]);

    const [departments, setDepartments] = useState([]);
    const [selectDeleteDepartments, setSelectDeleteDepartments] = useState([]);

    const [user, setUser] = useState([]);
    const [selectDeleteUser, setSelectDeleteUser] = useState([]);

    // Stepper state (create flow).
    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(ACCESS_DEVICES_TABS.ADD_DEVICES.key);
    const [searchElement, setSearchElement] = useState('');
    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    /**
     * Sets the active stepper step (create flow).
     * @param {number} step - Step index.
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
        setSearchElement("");
    }

    /**
     * Final validation before create/update.
     * @returns {boolean}
     */
    const validate = () => {
        if (!item.isValid(devices)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return false;
        }
        setEmptyFields(false);
        return true;
    }

    /**
     * Per-step validation used by the create stepper "next" buttons.
     * @param {number} step - Active step index.
     * @returns {boolean}
     */
    const handleValidateSteps = (step) => {
        if (step === STEPS_CREATE_SHIFT_ACCESS.ACCESS_DEVICE.INDEX && !item.isValid(devices)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return false;
        }
        setSearchElement("");
        setEmptyFields(false);
        return true;
    }

    /**
     * Clears only the resources owned by a given step.
     * @param {number} step - Step index.
     */
    const handleClearStep = (step) => {
        const clearMap = {
            [STEPS_CREATE_SHIFT_ACCESS.ACCESS_DEVICE.INDEX]: () => {
                setItem(prev => ({ ...prev, name: '' }));
                setSearchElement( '');
                setDevices([]);
                setModified(true)
            },
            [STEPS_CREATE_SHIFT_ACCESS.ACCESS_DEPARTMENTS.INDEX]: () => {
                setSearchElement( '');
                setDepartments([])
                setModified(true)
            },
            [STEPS_CREATE_SHIFT_ACCESS.CUSTOM_ACCESS.INDEX]: () => {
                setSearchElement( '');
                setUser([])
                setModified(true)
            },
        };
        clearMap[step]?.();
    }

    /**
     * Navigates back to the attendance list on the Shift Access subview.
     */
    const handleOnBack = () => {
        navigate(routes.attendance.main, {
            state: {
                currentTab: TABS_ATTENDANCE.MANAGE.INDEX,
                currentSubView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.SHIFT_ACCESS
            }
        });
    }

    return {
        id,
        t,
        DEV,
        permission,
        dispatch,
        navigate,
        state,

        item, setItem,
        disabled, setDisabled,
        emptyFields, setEmptyFields,
        handleChange,

        devices, setDevices,
        selectDeleteDevices, setSelectDeleteDevices,

        departments, setDepartments,
        selectDeleteDepartments, setSelectDeleteDepartments,

        user, setUser,
        selectDeleteUser, setSelectDeleteUser,

        steps, setSteps,
        activeStep, setActiveStep,
        handleStepChange,
        searchElement,
        setSearchElement,
        validate,
        handleValidateSteps,
        handleClearStep,
        handleOnBack,
        modified, setModified
    }
}

export default CommonShiftAccessApp
